import { useEffect, useId, useState, type ReactElement, type ReactNode } from 'react';

import { ModalShell } from '@/components/ui/ModalShell';

/** Motivo obligatorio de la acción (queda en la trazabilidad). */
export interface ConfirmReason {
  label: string;
  /** Mínimo de caracteres útiles (sin los espacios de los bordes). */
  min: number;
  max: number;
  placeholder?: string;
}

interface ConfirmModalProps {
  open: boolean;
  title: string;
  message: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  /** Estilo de acción destructiva (botón de confirmación en rojo). */
  destructive?: boolean;
  /** Operación en curso: deshabilita botones y bloquea cierre por ESC/backdrop. */
  busy?: boolean;
  /** Pide un motivo antes de confirmar; `onConfirm` lo recibe recortado. */
  reason?: ConfirmReason;
  /** Error de la acción, ya en palabras (qué pasó y qué hacer). */
  error?: string | null;
  onConfirm: (reason?: string) => void;
  onCancel: () => void;
}

/**
 * Modal de confirmación accesible y reusable (UX-COMP-01, UX-FB-01): antes de una
 * acción destructiva. Sobre ModalShell (role=dialog, focus trap, Escape y fondo que
 * cancelan salvo `busy`); foco inicial en Cancelar y descripción por el mensaje. Con
 * `reason` pide un motivo obligatorio (G2-C) y no confirma hasta que alcanza el mínimo.
 */
export function ConfirmModal({
  open,
  title,
  message,
  confirmLabel = 'Confirmar',
  cancelLabel = 'Cancelar',
  destructive = false,
  busy = false,
  reason,
  error = null,
  onConfirm,
  onCancel,
}: ConfirmModalProps): ReactElement | null {
  const descId = useId();
  const fieldId = useId();
  const [text, setText] = useState('');
  useEffect(() => {
    if (!open) {
      setText('');
    }
  }, [open]);
  const trimmed = text.trim();
  const ready = reason === undefined || trimmed.length >= reason.min;
  return (
    <ModalShell
      open={open}
      title={title}
      busy={busy}
      describedBy={descId}
      onClose={onCancel}
      footer={
        <>
          <button type="button" className="btn mut sm" onClick={onCancel} disabled={busy}>
            {cancelLabel}
          </button>
          <button
            type="button"
            className={`btn sm${destructive ? ' danger' : ''}`}
            onClick={() => onConfirm(reason === undefined ? undefined : trimmed)}
            disabled={busy || !ready}
          >
            {busy ? 'Procesando…' : confirmLabel}
          </button>
        </>
      }
    >
      <div className="modal-body">
        <div id={descId}>{message}</div>
        {reason !== undefined && (
          <>
            <label htmlFor={fieldId} className="fld-lab fld-gap">
              {reason.label}
            </label>
            <textarea
              id={fieldId}
              value={text}
              onChange={(e) => setText(e.target.value)}
              rows={3}
              maxLength={reason.max}
              placeholder={reason.placeholder}
              className="fld-full"
              disabled={busy}
              aria-describedby={`${fieldId}-h`}
            />
            <p id={`${fieldId}-h`} className="soft fld-hint">
              Al menos {reason.min} caracteres ({trimmed.length}/{reason.max}).
            </p>
          </>
        )}
        {error !== null && (
          <p role="alert" className="fld-err">
            {error}
          </p>
        )}
      </div>
    </ModalShell>
  );
}
